/**
 * LOT-UI-02 / DATA-01 — Dashboard directeur réseau M1/M2 (REP-V2).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ShoppingCart, Wrench, AlertTriangle, BarChart3, MessageCircle } from 'lucide-react';
import {
  DASH_PANEL,
  DASH_KPI,
  DASH_LINK,
  DASH_LINK_ACTIVE,
  DashboardSectionTitle,
} from '../../features/dashboard/dashboardV2Ui';

const RepairsBreakdown = ({ code, magasinStats }) => {
  const explicit = magasinStats?.repairs_in_progress_explicit;
  const legacy = magasinStats?.repairs_in_progress_legacy ?? 0;
  const total = magasinStats?.repairs_in_progress;

  if (total == null) return null;

  if (code === 'M1' && (explicit != null || legacy > 0)) {
    return (
      <div className="mt-2 space-y-0.5 text-[10px] text-gray-500 leading-snug" data-testid="directeur-m1-breakdown">
        <div>{explicit ?? 0} réparation{(explicit ?? 0) !== 1 ? 's' : ''} M1</div>
        {legacy > 0 && <div>{legacy} réparation{legacy !== 1 ? 's' : ''} legacy</div>}
        <div className="text-gray-400 font-medium pt-0.5">Total affiché : {total}</div>
      </div>
    );
  }

  if (code === 'M2') {
    return (
      <div className="mt-2 text-[10px] text-gray-500" data-testid="directeur-m2-breakdown">
        {total} réparation{total !== 1 ? 's' : ''} M2
      </div>
    );
  }

  return null;
};

const MagasinCard = ({ code, magasinStats, caisseOpen }) => (
  <div className={`${DASH_PANEL} p-4 sm:p-5 space-y-4`} data-testid={`directeur-magasin-${code}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <Building2 className="w-5 h-5 text-red-400" />
        <span className="text-xl font-bold text-white">{code}</span>
      </div>
      <span className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border ${
        caisseOpen ? 'bg-emerald-950/40 text-emerald-300 border-emerald-500/30' : 'bg-red-950/40 text-red-300 border-red-500/30'
      }`}>
        Caisse {caisseOpen ? 'ouverte' : 'fermée'}
      </span>
    </div>
    <div className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-white/10 bg-black/20 p-3">
        <ShoppingCart className="w-4 h-4 text-gray-400 mb-1" />
        <div className="text-2xl font-black tabular-nums text-white">{magasinStats?.ventes_jour ?? '—'}</div>
        <div className="text-[10px] text-gray-500 uppercase">Ventes jour</div>
      </div>
      <div className="rounded-lg border border-white/10 bg-black/20 p-3">
        <Wrench className="w-4 h-4 text-red-400 mb-1" />
        <div className="text-2xl font-black tabular-nums text-white">{magasinStats?.repairs_in_progress ?? '—'}</div>
        <div className="text-[10px] text-gray-500 uppercase">Répar. en cours</div>
        <RepairsBreakdown code={code} magasinStats={magasinStats} />
      </div>
    </div>
    <Link to={`/reparations?magasin=${code}`} className={`block text-center text-xs py-2.5 rounded-lg border border-white/10 text-gray-400 hover:text-white hover:border-white/20 bg-black/20`}>
      Voir réparations {code} →
    </Link>
  </div>
);

const DirecteurDashboard = ({ stats, caisseStatusByMagasin, openTicketsCount }) => {
  const byMagasin = stats?.by_magasin || {};
  const network = stats?.network || {};
  const networkRepairs = network.repairs_in_progress ?? stats?.repairs_in_progress ?? 0;
  const networkVentes = network.ventes_jour ?? stats?.ventes_jour ?? 0;

  return (
    <div className="space-y-5" data-testid="dashboard-directeur">
      <DashboardSectionTitle
        title="Vue réseau M1 + M2"
        subtitle="Vision consolidée — legacy comptés M1 à l'affichage (sans migration)"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MagasinCard code="M1" magasinStats={byMagasin.M1} caisseOpen={!!caisseStatusByMagasin?.M1?.is_open} />
        <MagasinCard code="M2" magasinStats={byMagasin.M2} caisseOpen={!!caisseStatusByMagasin?.M2?.is_open} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { v: networkVentes, l: "Ventes aujourd'hui (réseau)" },
          { v: networkRepairs, l: 'Réparations en cours (réseau)' },
          { v: stats?.stock_faible || 0, l: 'Stock faible' },
          { v: stats?.ruptures || 0, l: 'Ruptures', danger: true },
        ].map((k) => (
          <div key={k.l} className={DASH_KPI}>
            <div className={`text-2xl font-black tabular-nums ${k.danger ? 'text-red-400' : 'text-white'}`}>{k.v}</div>
            <div className="text-[10px] text-gray-500 uppercase mt-1">{k.l}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Link to="/stats" className={DASH_LINK}><BarChart3 className="w-4 h-4" /> Rapports</Link>
        <Link to="/reparations" className={DASH_LINK}><Wrench className="w-4 h-4" /> Réparations</Link>
        <Link to="/stock" className={DASH_LINK}><AlertTriangle className="w-4 h-4" /> Stock</Link>
        {openTicketsCount > 0 && (
          <Link to="/chat" className={DASH_LINK_ACTIVE}><MessageCircle className="w-4 h-4" /> {openTicketsCount} ticket(s)</Link>
        )}
      </div>
    </div>
  );
};

export default DirecteurDashboard;
